import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { AsyncButton } from "../components/AsyncButton";
import { DateInput } from "../components/DateInput";
import { formatDate, formatDateTime, todayStr } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { futureTimeSlots } from "../utils/timeSlots";
import {
  getAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement, markAnnouncementRead,
  aiWriteAnnouncement,
} from "../api";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

const CATEGORIES = [
  { value: "general",  label: "General",       bg: "#eff6ff", fg: "#1e3a8a" },
  { value: "policy",   label: "Policy Update",  bg: "#f5f3ff", fg: "#7c3aed" },
  { value: "event",    label: "Event",          bg: "#ecfeff", fg: "#0891b2" },
  { value: "holiday",  label: "Holiday",        bg: "#ecfdf5", fg: "#059669" },
  { value: "urgent",   label: "Urgent",         bg: "#fef2f2", fg: "#dc2626" },
];

function catStyle(value: string) {
  return CATEGORIES.find(c => c.value === value) || CATEGORIES[0];
}

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const { toast } = useToast();
  const confirm = useConfirm();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 480;
  const canManage = ["admin", "admin_assistant", "hr"].includes(user?.role || "");

  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [filterCat, setFilterCat] = useState<string>("all");

  // Compose modal
  const [showCompose, setShowCompose] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);
  const [category, setCategory] = useState("general");
  const [expiresAt, setExpiresAt] = useState("");
  const [saving, setSaving] = useState(false);

  // Scheduling
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [publishDate, setPublishDate] = useState("");
  const [publishTime, setPublishTime] = useState("");

  // AI write
  const [showAI, setShowAI] = useState(false);
  const [aiTopic, setAITopic] = useState("");
  const [aiTone, setAITone] = useState("friendly");
  const [aiLoading, setAILoading] = useState(false);

  async function load(cat?: string) {
    try {
      const params = cat && cat !== "all" ? `?category=${cat}` : "";
      const data = await getAnnouncements(params);
      setItems(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }

  useEffect(() => { load(filterCat); }, [filterCat]);

  function openCompose(a?: any) {
    setEditing(a || null);
    setTitle(a?.title || "");
    setBody(a?.body || "");
    setPinned(a?.pinned || false);
    setCategory(a?.category || "general");
    setExpiresAt(a?.expires_at ? a.expires_at.substring(0, 10) : "");
    setScheduleEnabled(false);
    setPublishDate("");
    setPublishTime("");
    setShowCompose(true);
  }

  async function handleAIWrite() {
    if (!aiTopic.trim()) { toast("Describe the topic first", "error"); return; }
    setAILoading(true);
    try {
      const result = await aiWriteAnnouncement({ topic: aiTopic, tone: aiTone, category });
      setTitle(result.title);
      setBody(result.body);
      setShowAI(false);
      setAITopic("");
      toast("AI draft ready — review and post when happy", "success");
    } catch { toast("AI generation failed — please retry", "error"); }
    finally { setAILoading(false); }
  }

  async function handleSave() {
    if (!title.trim() || !body.trim()) { toast("Title and message are required", "error"); return; }
    const isImmediatePost = !editing && !(scheduleEnabled && publishDate && publishTime);
    if (isImmediatePost) {
      const ok = await confirm(
        "This will immediately email every active employee in your company. Continue?",
        { title: "Post announcement", confirmLabel: "Send to Everyone", danger: false }
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateAnnouncement(editing.id, { title, body, pinned, category, expires_at: expiresAt || null });
      } else {
        await createAnnouncement({
          title, body, pinned, category,
          expires_at: expiresAt || null,
          publish_at: (scheduleEnabled && publishDate && publishTime) ? new Date(`${publishDate}T${publishTime}`).toISOString() : null,
        });
      }
      toast(editing ? "Announcement updated" : "Announcement posted", "success");
      setShowCompose(false);
      load(filterCat);
    } catch { toast("Failed to save", "error"); }
    finally { setSaving(false); }
  }

  async function handleDelete(a: any) {
    const ok = await confirm(`Delete announcement "${a.title}"?`, { confirmLabel: "Delete", danger: true });
    if (!ok) return;
    await deleteAnnouncement(a.id);
    toast("Deleted", "success");
    load(filterCat);
  }

  function toggleExpand(a: any) {
    const next = expanded === a.id ? null : a.id;
    setExpanded(next);
    if (next && !a.is_read) {
      markAnnouncementRead(a.id).then(() =>
        setItems(prev => prev.map(x => x.id === a.id ? { ...x, is_read: true } : x))
      ).catch(() => {});
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0",
    fontSize: 14, boxSizing: "border-box",
  };

  return (
    <div style={{ padding: isMobile ? 16 : 24, maxWidth: 800, margin: "0 auto" }}>
      {/* The house `.page-header` block. A hand-rolled navy <h2> here read as a
          different kind of heading from the black <h1> every sibling page uses. */}
      <div className="page-header">
        <div>
          <h1>Announcements</h1>
          <p>Company news and updates</p>
        </div>
        {canManage && (
          <button onClick={() => openCompose()}
            style={{ background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white", border: "none", padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
            + New Announcement
          </button>
        )}
      </div>

      {/* Category filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {[{ value: "all", label: "All" }, ...CATEGORIES].map(c => (
          <button key={c.value} onClick={() => setFilterCat(c.value)}
            style={{
              padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
              border: filterCat === c.value ? `1.5px solid ${NAVY}` : "1.5px solid #e2e8f0",
              background: filterCat === c.value ? NAVY : "white",
              color: filterCat === c.value ? "white" : "#475569",
            }}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8", background: "#f8fafc", borderRadius: 12 }}>
          <div style={{ fontSize: 40 }}>📢</div>
          <div style={{ marginTop: 10 }}>No announcements{filterCat !== "all" ? " in this category" : " yet"}.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {items.map(a => {
            const cs = catStyle(a.category);
            return (
              <div key={a.id} onClick={() => toggleExpand(a)} style={{
                background: "white", borderRadius: 12, padding: "16px 20px", cursor: "pointer",
                border: a.is_read ? "1px solid #e2e8f0" : `1.5px solid ${TEAL}`,
                boxShadow: a.is_read ? "none" : "0 2px 12px rgba(0,201,167,0.12)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    {a.pinned && <span style={{ fontSize: 13 }}>📌</span>}
                    {/* Category badge */}
                    <span style={{
                      background: cs.bg, color: cs.fg, fontSize: 10, fontWeight: 700,
                      padding: "2px 8px", borderRadius: 10, textTransform: "uppercase", letterSpacing: "0.4px",
                    }}>{cs.label}</span>
                    <span style={{ fontWeight: a.is_read ? 600 : 800, color: "#0f172a", fontSize: 15 }}>{a.title}</span>
                    {!a.is_published && a.publish_at && (
                      <span style={{ background: "#f59e0b", color: "white", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>
                        🕐 {formatDateTime(a.publish_at, tz)}
                      </span>
                    )}
                    {a.is_published && !a.is_read && <span style={{ background: TEAL, color: "white", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>NEW</span>}
                  </div>
                  <span style={{ fontSize: 12, color: "#94a3b8", whiteSpace: "nowrap" }}>
                    {formatDate(a.created_at, "short", tz)}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                  by {a.author_name || "HR"}
                  {canManage && a.read_count != null && (
                    <> · read by <span style={{ color: a.read_count === a.headcount ? "#059669" : "#64748b", fontWeight: 600 }}>
                      {a.read_count}/{a.headcount}
                    </span> employees</>
                  )}
                </div>
                {expanded === a.id && (
                  <>
                    <div style={{ fontSize: 14, color: "#334155", marginTop: 12, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{a.body}</div>
                    {a.expires_at && (
                      <div style={{ fontSize: 12, color: "#f59e0b", marginTop: 8 }}>
                        ⏱ Expires {formatDate(a.expires_at, "short", tz)}
                      </div>
                    )}
                    {canManage && (
                      <div style={{ display: "flex", gap: 8, marginTop: 14 }} onClick={e => e.stopPropagation()}>
                        <button onClick={() => openCompose(a)}
                          style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Edit</button>
                        <AsyncButton onClick={() => handleDelete(a)}
                          style={{ background: "#fee2e2", color: "#b91c1c", border: "none", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Delete</AsyncButton>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Compose / Edit modal */}
      {showCompose && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, maxWidth: 540, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: NAVY }}>{editing ? "Edit" : "New"} Announcement</h3>
              {!editing && (
                <button onClick={() => setShowAI(v => !v)}
                  style={{ background: showAI ? NAVY : "linear-gradient(135deg,#7c3aed,#a855f7)", color: "white", border: "none", padding: "7px 14px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                  ✨ {showAI ? "Hide AI" : "Write with AI"}
                </button>
              )}
            </div>

            {/* AI write panel */}
            {showAI && (
              <div style={{ background: "linear-gradient(135deg,#f5f3ff,#ede9fe)", borderRadius: 10, padding: 16, marginBottom: 16, border: "1.5px solid #c4b5fd" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#7c3aed", marginBottom: 10 }}>✨ AI Announcement Writer</div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>What's the announcement about?</label>
                <textarea value={aiTopic} onChange={e => setAITopic(e.target.value)} rows={3}
                  placeholder="e.g. Office closed on 20 June for public holiday. Remind team to plan work ahead."
                  style={{ ...inputStyle, marginBottom: 10, resize: "none" }} />
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Tone</label>
                    <select value={aiTone} onChange={e => setAITone(e.target.value)} style={{ ...inputStyle, width: "auto", minWidth: 140 }}>
                      <option value="friendly">😊 Friendly</option>
                      <option value="formal">🎩 Formal</option>
                      <option value="urgent">🚨 Urgent</option>
                    </select>
                  </div>
                  <button disabled={aiLoading} onClick={handleAIWrite}
                    style={{ background: "#7c3aed", color: "white", border: "none", padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13, alignSelf: "flex-end" }}>
                    {aiLoading ? "Writing…" : "Generate Draft"}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: "#8b5cf6", marginTop: 8 }}>Uses 1 AI credit · You can edit the result before posting</div>
              </div>
            )}

            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Announcement title"
              style={{ ...inputStyle, marginBottom: 12 }} />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Message *</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={6} placeholder="Write your announcement…"
              style={{ ...inputStyle, marginBottom: 12, resize: "vertical" }} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)} style={inputStyle}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Expires on (optional)</label>
                <DateInput value={expiresAt} onChange={e => setExpiresAt(e.target.value)}
                  style={inputStyle} />
              </div>
            </div>

            {!editing && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", cursor: "pointer", marginBottom: scheduleEnabled ? 8 : 0 }}>
                  <input type="checkbox" checked={scheduleEnabled} onChange={e => setScheduleEnabled(e.target.checked)} />
                  🕐 Schedule for later
                </label>
                {scheduleEnabled && (
                  <div style={{ marginTop: 6, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, maxWidth: 360 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Publish date *</label>
                      <DateInput value={publishDate} min={todayStr()}
                        onChange={e => setPublishDate(e.target.value)}
                        style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Publish time *</label>
                      <select value={publishTime} onChange={e => setPublishTime(e.target.value)} style={{ ...inputStyle, background: "white" }}>
                        <option value="">Select...</option>
                        {futureTimeSlots(publishDate, todayStr()).map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8", gridColumn: "1 / -1" }}>Employees will be notified by email + in-app at this time.</div>
                  </div>
                )}
              </div>
            )}

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", marginBottom: 18, cursor: "pointer" }}>
              <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} />
              📌 Pin to top
            </label>

            {!editing && (
              <div style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 14px", marginBottom: 18, fontSize: 12, color: "#64748b" }}>
                {scheduleEnabled
                  ? "📅 Email and in-app notifications will go out at the scheduled time."
                  : "📧 An email notification will be sent to all employees when you post."}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowCompose(false)}
                style={{ background: "#f1f5f9", color: "#475569", border: "none", padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button disabled={saving} onClick={handleSave}
                style={{ background: NAVY, color: "white", border: "none", padding: "9px 20px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                {saving ? "Saving…" : editing ? "Save Changes" : scheduleEnabled ? "Schedule" : "Post Announcement"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
